import {Component, ElementRef, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {map, Observable} from "rxjs";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {MatCheckboxChange, MatCheckbox} from "@angular/material/checkbox";
import {MepPopoverDirective} from "src/app/shared/mep-popover/mep-popover.directive";
import {NgClass, AsyncPipe, DatePipe} from "@angular/common";
import {SkillDbListComponent} from "../../../../../shared/skill-db-list/components/skill-db-list.component";
import {MatTooltip} from "@angular/material/tooltip";
import {MatSelect} from "@angular/material/select";
import {MatOption} from "@angular/material/autocomplete";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";

/**
 * to use this component you have to pass the following inputs:
 * - originalList: T[] = [];
 * - map: (originalList: T[]) => \{...\}[];
 *
 * and the following outputs:
 * - delete: new EventEmitter();
 * - add: new EventEmitter();
 *
 */
@Component({
	selector: "app-list-picker",
	templateUrl: "./list-picker.component.html",
	styleUrls: ["./list-picker.component.scss"],
	imports: [
		NgClass,
		MepPopoverDirective,
		SkillDbListComponent,
		MatTooltip,
		MatSelect,
		MatOption,
		MatCheckbox,
		MatButton,
		MatIcon,
		AsyncPipe,
		DatePipe,
		TranslatePipe,
	],
})
export class ListPickerComponent<T> implements OnInit {
	/**
	 * an observable of the original list
	 */
	@Input() originalList: Observable<T[]>;
	/**
	 * maps the original list to be in the format of the list picker
	 */
	@Input() map: (originalList: T[]) => {
		firstColumn: string;
		firstColumnDate: string;
		secondColumn: string;
		secondColumnLink?: () => void;
		profileViewId?: number;
		thirdColumn: string;
		status;
		toggle: boolean;
		original: T;
	}[];
	/**
	 * location of a profile picture
	 * -- optional
	 */
	@Input() profilePicture?: (original: T, api: string, profilePictureFallback: string) => string;
	/**
	 * Add button Text
	 */
	@Input() addButtonTooltip: string;
	@Input() title: string;
	@Input() toggleTitle: string;
	@Input() toggleTooltip: string;
	@Input() disabled = false;
	@Input() addKeyIconToLabel = false;
	@Input() toggleText: string;
	/**
	 *
	 */
	@Input() toggle = false;
	@Input() possibleSelection = [];
	@Input() selectionTranslate: string;
	@Input() filter;
	@Input() canChangeStatus = true;
	@Input() disableDeleteButtonByStatus = [];
	@Input() sortOff = false;
	@Input() showProfileViewButton = false;
	@Input() profileViewButtonTooltip: string;

	@Input() removeAddButton = false;
	/**
	 *
	 *
	 * Show Skills of Employees
	 */
	@Input() showSkillsOfEmployee = false;
	/**
	 *
	 * options:
	 * * "1"-"2"-"3"-"4" -> 1-4 times a mat-form-field height (approximately)
	 * * everything else -> custom height in css style: 'height: ${height}'
	 */
	@Input() height = "100%";
	/**
	 * Show transfer to project button for each entry
	 */
	@Input() showTransferToProjectButton = false;
	/**
	 * Text for the transfer to project button
	 */
	@Input() transferToProjectButtonText: string;
	/**
	 * Tooltip for the transfer to project button
	 */
	@Input() transferToProjectButtonTooltip: string | ((original: T) => string);
	/**
	 * delete event has to remove the element from the original list for changes
	 *
	 * important do not overwrite the originallist
	 */
	@Output() delete = new EventEmitter<T>();
	/**
	 * add event has to add the element to the original list for changes
	 *
	 * important do not overwrite the originallist
	 */
	@Output() add = new EventEmitter();

	@Output() statusChange = new EventEmitter();

	@Output() toggleChange = new EventEmitter();

	@Output() transferToProjectClick = new EventEmitter<T>();

	@Output() profileViewClick = new EventEmitter<number>();

	profilePictureLink = "https://msggroup.sharepoint.com/_layouts/15/userphoto.aspx?size=L&username=";
	profilePictureFallback = "assets/mensch.svg";
	imgFallback = false;
	popoverTimeouts = new Map<number, ReturnType<typeof setTimeout>>();

	skillDbHoveredEmployeeId: number;

	list: Observable<
		{
			firstColumn: string;
			firstColumnDate: string;
			secondColumn: string;
			secondColumnLink?: () => void;
			profileViewId?: number;
			thirdColumn: string;
			status;
			toggle: boolean;
			original: T;
		}[]
	>;

	constructor(
		private _elementRef: ElementRef,
		public translate: TranslateService,
	) {}

	ngOnInit(): void {
		this._elementRef.nativeElement.style.setProperty("--list-picker-height", this.getHeight(false));
		this._elementRef.nativeElement.style.setProperty("--list-picker-height-zoom", this.getHeight(true));

		this.getFilterItems().then((filter) => {
			if (filter) {
				this.list = this.originalList.pipe(
					map((value) => {
						let mappedList = this.map(value);
						if (!this.sortOff) {
							mappedList = mappedList.sort((a, b) =>
								a.firstColumnDate
									? new Date(b.firstColumnDate).getTime() - new Date(a.firstColumnDate).getTime()
									: a.firstColumn.localeCompare(b.firstColumn),
							);
						}
						return mappedList.filter((item) => this.filterFunction(item, filter));
					}),
				);
			} else {
				this.list = this.originalList.pipe(
					map((value) => {
						let mappedList = this.map(value);
						if (!this.sortOff) {
							mappedList = mappedList.sort((a, b) =>
								a.firstColumnDate
									? new Date(b.firstColumnDate).getTime() - new Date(a.firstColumnDate).getTime()
									: a.firstColumn.localeCompare(b.firstColumn),
							);
						}
						return mappedList;
					}),
				);
			}
		});
	}

	async getFilterItems() {
		const filter = await this.filter;

		if (!filter) {
			return null;
		}

		const properties = ["firstColumn", "secondColumn", "thirdColumn", "status"];
		let toFilter: {[key: string]: unknown[]} = {};

		properties.forEach((prop) => {
			if (filter[prop]) {
				toFilter[prop] = Array.isArray(filter[prop]) ? filter[prop] : [filter[prop]];
			}
		});

		return toFilter;
	}

	filterFunction(item, filter): boolean {
		let filterOut =
			(!filter.firstColumn || filter.firstColumn.includes(item.firstColumn)) &&
			(!filter.secondColumn || filter.secondColumn.includes(item.secondColumn)) &&
			(!filter.thirdColumn || filter.thirdColumn.includes(item.thirdColumn)) &&
			(!filter.status || filter.status.includes(item.status));
		return filterOut;
	}

	getHeight(zoom: boolean): string {
		switch (this.height) {
			case "1":
				return zoom ? "4.57rem" : "4.97rem";
			case "1.5":
				return zoom ? "6.9rem" : "6.9rem";
			case "2":
				return zoom ? "8.8rem" : "9.9rem";
			case "2.5":
				return zoom ? "15.5rem" : "14.7rem";
			case "3":
				return zoom ? "19.7rem" : "19.7rem";
			case "3.2":
				return zoom ? "19.8rem" : "18.4rem";
			case "4":
				return zoom ? "20.9rem" : "19.4rem";
			default:
				return `${this.height}`;
		}
	}

	getCreatedByUser(entry) {
		if (entry.original.createdByUser) {
			return this.translate.instant("mep.components.list-picker.created_by_user") + entry.original.createdByUser;
		}
	}

	checkDisabled(emitter: EventEmitter<T>, properties: T = null) {
		if (!this.disabled) {
			emitter.emit(properties);
		}
	}

	removeDeleteButton(entry) {
		return this.disableDeleteButtonByStatus.includes(entry.status);
	}

	statusChanged(original: T, properties: T = null) {
		if (!this.disabled) {
			this.statusChange.emit({original, properties});
		}
	}

	checkBoxChanged(original: T, toggle: MatCheckboxChange) {
		this.toggleChange.emit({original, toggle: toggle.checked});
	}

	openPopover(popover: MepPopoverDirective, employeeId: number) {
		// Re-entering the trigger (e.g. natural mouse jitter on the way to the popup content)
		// must cancel any close already scheduled for this row - otherwise it fires ~200ms later
		// regardless and closes the popover shortly after it was "reopened".
		this.cancelClosePopover(employeeId);
		this.skillDbHoveredEmployeeId = employeeId;
		popover.open();
	}

	closePopover(popover: MepPopoverDirective, employeeId: number) {
		const timeout = setTimeout(() => {
			popover.close();
			// Only clear the shared hovered-id if it's still this row's - another row may have
			// become the active hover target while this close was pending.
			if (this.skillDbHoveredEmployeeId === employeeId) {
				this.skillDbHoveredEmployeeId = null;
			}
			this.popoverTimeouts.delete(employeeId);
		}, 200);
		this.popoverTimeouts.set(employeeId, timeout);
	}

	cancelClosePopover(employeeId: number) {
		clearTimeout(this.popoverTimeouts.get(employeeId));
		this.popoverTimeouts.delete(employeeId);
	}

	getTransferToProjectButtonTooltip(entry): string {
		if (typeof this.transferToProjectButtonTooltip === "function") {
			return this.transferToProjectButtonTooltip(entry.original);
		}
		return this.transferToProjectButtonTooltip;
	}

	onTransferToProjectButtonClick(original: T) {
		if (!this.disabled) {
			this.transferToProjectClick.emit(original);
		}
	}

	onProfileViewButtonClick(employeeId: number, event: MouseEvent) {
		event.stopPropagation();
		if (!this.disabled && employeeId) {
			this.profileViewClick.emit(employeeId);
		}
	}
}
